import { IDT } from '@seen/core/models/care-teams/idt'
import {
  type CitableSourceResource,
  isSignedCitableSource,
} from '@seen/core/models/compositionNote'
import { ExtendedComposition } from '@seen/core/models/compositions/composition'
import { Diagnosis } from '@seen/core/models/conditions/Diagnosis'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { ExtendedImmunization } from '@seen/core/models/immunizations/ExtendedImmunization'
import { ExtendedMedicationRequest } from '@seen/core/models/medicationRequest'
import { ExtendedObservation } from '@seen/core/models/observation'
import { Order } from '@seen/core/models/orders/Order'
import { Participant } from '@seen/core/models/patients/participant'
import { ExtendedQuestionnaireResponse } from '@seen/core/models/questionnaireResponse'
import { extractId, parseReferenceString } from '@seen/core/utils'
import { useOptionalParticipantReference } from '#src/contexts/current-participant/useOptionalParticipantReference.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import {
  isCitedSourceOnChart,
  suggestionSourceLabel,
} from '#src/utils/suggestionSourceLabel.ts'

/* Where a citation stands once its reference has been followed. Only `citable`
 * shows anything; the rest are kept apart so a surface — and a test — can say
 * which silence it is looking at, because they are not the same silence: a
 * read in flight will settle, an unsigned draft may yet be signed, and a
 * reference to somebody else's chart must never resolve at all. */
export type SuggestionSourceState =
  /* The read has not settled. */
  | 'loading'
  /* The reference names nothing this clinician can read: a resource that does
   * not exist, cannot be fetched, or is not a kind the notes list would show. */
  | 'unreadable'
  /* The resource was not confirmed to be about the open participant: it
   * resolved to a different chart, it carries no subject at all, or there is no
   * participant in context to check it against. */
  | 'unconfirmed-participant'
  /* The resource is real and on this chart, but nobody has signed it — or, for
   * a validated-tool response, nobody has signed the assessment holding it; for
   * a structured record, it is not finished — a draft or an entry in error, a
   * condition refuted, a reading not final, or a care team no longer in force. */
  | 'unsigned'
  /* Signed and on this chart; `label` names it. */
  | 'citable'

export interface ResolvedSuggestionSource {
  state: SuggestionSourceState
  /* The chip's text, present exactly when the state is `citable`. */
  label?: string
}

interface CitedSourceRead {
  isError: boolean
  data?: { toFhirResource(): CitableSourceResource }
}

/* The chip's text for the resource an AI proposal cited, read so the chip can
 * be named from it rather than from the label the model wrote, and so the
 * citation can be checked against the participant whose chart is open. The
 * naming and the signature gate both live in `suggestionSourceLabel`; this owns
 * the fetching and the subject check that depends on it. */
export function useResolvedSuggestionSource(
  source: { reference: string } | undefined
): ResolvedSuggestionSource {
  const reference = source?.reference
  const type = reference ? parseReferenceString(reference)?.type : undefined
  const participantReference = useOptionalParticipantReference()?.reference

  /* One read per citable type rather than one dynamic read: `useRead` takes a
   * concrete resource class, and it disables itself for an undefined id, so the
   * ones that do not match the reference never fetch. The classes are the ones
   * the chart's own mention chips read with, so a chip and its excerpt share one
   * cached read.
   *
   * Errors are silent because a reference is only as good as the model that
   * wrote it: one naming a resource that never existed or that this clinician
   * cannot read is exactly the case the chip is meant to swallow, and the
   * clinician never asked for the lookup. */
  const composition = useRead(
    ExtendedComposition,
    type === 'Composition' ? reference : undefined,
    { silentErrors: true }
  )
  const questionnaireResponse = useRead(
    ExtendedQuestionnaireResponse,
    type === 'QuestionnaireResponse' ? reference : undefined,
    { silentErrors: true }
  )
  const documentReference = useRead(
    ExtendedDocumentReference,
    type === 'DocumentReference' ? reference : undefined,
    { silentErrors: true }
  )
  const careTeam = useRead(IDT, type === 'CareTeam' ? reference : undefined, {
    silentErrors: true,
  })
  const observation = useRead(
    ExtendedObservation,
    type === 'Observation' ? reference : undefined,
    { silentErrors: true }
  )
  const condition = useRead(
    Diagnosis,
    type === 'Condition' ? reference : undefined,
    { silentErrors: true }
  )
  const medicationRequest = useRead(
    ExtendedMedicationRequest,
    type === 'MedicationRequest' ? reference : undefined,
    { silentErrors: true }
  )
  const serviceRequest = useRead(
    Order,
    type === 'ServiceRequest' ? reference : undefined,
    { silentErrors: true }
  )
  const immunization = useRead(
    ExtendedImmunization,
    type === 'Immunization' ? reference : undefined,
    { silentErrors: true }
  )
  const participant = useRead(
    Participant,
    type === 'Patient' ? reference : undefined,
    { silentErrors: true }
  )

  /* The composition that names this response in a section, if any. The link
   * runs one way — nothing is written back onto the response — so the parent is
   * only reachable by searching for the record that names it. Any kind of
   * composition counts: the editor embeds the same validated tools in a visit
   * note as in an assessment, and a tool sitting in an unsigned note must be as
   * uncitable as one sitting in an unsigned assessment.
   *
   * Searched under the canonical reference, the same key `getParticipantValidatedToolResponses`
   * builds: the model writes this citation, and a versioned reference would
   * otherwise find no parent and read as a standalone response. */
  const parentCompositions = useSearch(
    ExtendedComposition,
    reference && participantReference
      ? {
          ...ExtendedComposition.byEntry([
            `QuestionnaireResponse/${extractId(reference)}`,
          ]),
          /* Scoped to the open chart, as the tool's parent lookup is. Content
             copied between charts carries the editor node's response reference
             with it, so another participant's record can name this response;
             unscoped, that record's signature would decide whether this
             citation resolves. */
          subject: participantReference,
        }
      : undefined,
    {
      /* Silent for the same reason the reads above are: the chip swallows a
       * parent it cannot reach, so a failed or forbidden search must not raise
       * a toast over a lookup the clinician never asked for. */
      silentErrors: true,
      query: {
        enabled:
          type === 'QuestionnaireResponse' &&
          !!reference &&
          !!participantReference,
      },
    }
  )

  const read = new Map<string | undefined, CitedSourceRead>([
    ['Composition', composition],
    ['QuestionnaireResponse', questionnaireResponse],
    ['DocumentReference', documentReference],
    ['CareTeam', careTeam],
    ['Observation', observation],
    ['Condition', condition],
    ['MedicationRequest', medicationRequest],
    ['ServiceRequest', serviceRequest],
    ['Immunization', immunization],
    ['Patient', participant],
  ]).get(type)
  if (!read || read.isError) {
    return { state: 'unreadable' }
  }
  if (!read.data) {
    return { state: 'loading' }
  }

  const resources = {
    composition: composition.data,
    questionnaireResponse: questionnaireResponse.data,
    documentReference: documentReference.data,
    careTeam: careTeam.data,
    observation: observation.data,
    condition: condition.data,
    medicationRequest: medicationRequest.data,
    serviceRequest: serviceRequest.data,
    immunization: immunization.data,
    participant: participant.data,
    parentCompositions: parentCompositions.data,
  }

  /* Confirmed to be this participant's, or it is not cited. A subject that
   * names someone else and a subject that names nobody are the same answer
   * here: neither establishes that the source belongs on the open chart, and a
   * citation is a claim that it does. Checked ahead of the parent search: whose
   * record it is does not depend on who signed it. */
  if (!isCitedSourceOnChart(resources, participantReference)) {
    return { state: 'unconfirmed-participant' }
  }

  /* An unsettled parent search is not the same as "no parent". Until it
   * succeeds there is nothing to say whether this response belongs to an
   * unsigned assessment, and an empty list would read as "standalone, judge it
   * on its own status" — showing a chip for the whole time the search is in
   * flight, and keeping it if the search fails. Nothing until the answer is
   * known. */
  if (type === 'QuestionnaireResponse') {
    if (parentCompositions.isError) {
      return { state: 'unreadable' }
    }
    if (!parentCompositions.isSuccess) {
      return { state: 'loading' }
    }
  }

  const signed =
    isSignedCitableSource(read.data.toFhirResource()) &&
    !(parentCompositions.data ?? []).some(
      (parent) => !isSignedCitableSource(parent.toFhirResource())
    )
  if (!signed) {
    return { state: 'unsigned' }
  }

  /* A signed resource that still names nothing is one the notes list would not
   * show — a care-plan snapshot, say — which is not a note to cite either. */
  const label = suggestionSourceLabel(resources)
  return label ? { state: 'citable', label } : { state: 'unreadable' }
}
